// TASK-9808 — "Compliance partner AI access" card on the Account view (owner).
//
// The carrier's OWNER approves or turns off AI access for the compliance
// partner firm that manages the account (Chris 2026-10-13: "Allow with a
// consent step"). The server decides everything: getPartnerAiConsent /
// setPartnerAiConsent are owner-only (tenants/{tid}.ownerId, tenant from the
// auth claim) and the connector re-reads the consent on every call, so a
// "Turn off" takes effect on the partner's next request.
//
// Renders nothing for a login the server refuses (not the owner) or for an
// account with no compliance partner on file. Firm names come from Firestore,
// so every string is escaped before it touches innerHTML; handlers bind by
// data-attribute, never an inline onclick with interpolated values.
// No imports: the host passes { functions, httpsCallable } (CLAUDE.md pitfall #1).

const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const toast = (m, t) => window.showToast?.(m, t || 'info');

export function mountPartnerAiConsent(el, { functions, httpsCallable, isDemo }) {
  if (!el) return;
  if (isDemo) { el.innerHTML = ''; return; }
  const call = (name, data) => httpsCallable(functions, name)(data || {}).then((r) => r.data);
  let view = null;
  let busy = false;
  // TASK-9820 — the scope text comes from the SERVER (the same text the
  // consent record stores), never a copy in this file.
  const scopeText = () => String((view && view.scope && view.scope.text) || '');

  function render() {
    if (view && !view.managed && Array.isArray(view.staleGrants) && view.staleGrants.length) {
      // COD r1 (Domain) — no live partner, but an old approval is still on file.
      el.innerHTML = `
        <div class="account-section-title"><span class="material-icons icon-dd-ai" aria-hidden="true"></span> Compliance partner AI access</div>
        <p style="font-size:0.9rem;color:var(--text-primary);margin:0 0 8px;">An earlier approval for a compliance partner's AI is still on file. That partner no longer manages your account, so it is not in use. You can turn it off for good.</p>
        ${view.staleGrants.map((g) => `<div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-top:8px;">
          <span style="font-size:0.9rem;color:var(--text-primary);">${esc(g.firmName)}</span>
          <button type="button" class="btn btn-secondary" data-pac-stale="${esc(g.firmUid)}" style="min-height:48px;"><span class="material-icons" style="font-size:18px;">block</span> Turn off</button>
        </div>`).join('')}`;
      el.querySelectorAll('button').forEach((b) => { b.disabled = busy; });
      return;
    }
    if (!view || !view.managed || !view.firm) { el.innerHTML = ''; return; }
    const firm = esc(view.firm.firmName || 'Your compliance partner');
    const c = view.consent || { status: 'none' };
    const on = c.status === 'granted';
    let line;
    if (on) line = `Approved${c.grantedByName ? ` by ${esc(c.grantedByName)}` : ''}${c.grantedAtMs ? ` on ${esc(fmtDate(c.grantedAtMs))}` : ''}.`;
    else if (c.status === 'revoked') line = `Turned off${c.revokedReason === 'partner_unlinked' ? ' when the partner was removed' : (c.revokedByName ? ` by ${esc(c.revokedByName)}` : '')}${c.revokedAtMs ? ` on ${esc(fmtDate(c.revokedAtMs))}` : ''}.`;
    else line = 'Not approved.';
    el.innerHTML = `
      <div class="account-section-title">
        <span class="material-icons icon-dd-ai" aria-hidden="true"></span> Compliance partner AI access
      </div>
      <p style="font-size:0.9rem;color:var(--text-primary);margin:0 0 8px;">
        ${firm} manages your compliance. Their staff may want to use their own AI assistant (Claude) in your account.
        It can only do that if you approve it here. You can turn it off any time, and it stops on their next request.
      </p>
      <p style="font-size:0.9rem;color:var(--text-primary);margin:0 0 8px;"><strong>What it can see:</strong> ${esc(scopeText())}</p>
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
        <span class="badge ${on ? 'badge-active-green' : ''}" style="${on ? '' : 'background:var(--bg-secondary,#f1f5f9);color:var(--text-primary);'}">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">${on ? 'check_circle' : 'block'}</span> ${on ? 'On' : 'Off'}
        </span>
        <span style="font-size:0.85rem;color:var(--text-secondary);">${line}</span>
      </div>
      <div style="margin-top:12px;">
        ${on
          ? '<button type="button" class="btn btn-secondary" data-pac-set="off" style="min-height:48px;"><span class="material-icons" style="font-size:18px;">block</span> Turn off</button>'
          : `<button type="button" class="btn btn-primary" data-pac-set="on" style="min-height:48px;"><span class="material-icons" style="font-size:18px;">check</span> Approve for ${firm}</button>`}
      </div>`;
    el.querySelectorAll('button').forEach((b) => { b.disabled = busy; });
  }

  async function load() {
    try {
      view = await call('getPartnerAiConsent');
    } catch (e) {
      // Not the owner (or no company) — nothing to show.
      view = null;
    }
    render();
  }

  el.addEventListener('click', async (ev) => {
    const staleBtn = ev.target.closest?.('[data-pac-stale]');
    if (staleBtn && el.contains(staleBtn) && !busy) {
      busy = true; render();
      try {
        await call('setPartnerAiConsent', { grant: false, firmUid: staleBtn.dataset.pacStale });
        toast('Turned off.', 'success');
      } catch (e) {
        toast((e && e.message) || 'Nothing was changed. Try again.', 'error');
      } finally { busy = false; await load(); }
      return;
    }
    const btn = ev.target.closest?.('[data-pac-set]');
    if (!btn || !el.contains(btn) || busy || !view || !view.firm) return;
    const grant = btn.dataset.pacSet === 'on';
    const firmName = view.firm.firmName || 'your compliance partner';
    if (grant && typeof window.showConfirm === 'function') {
      const ok = await window.showConfirm(
        'Approve AI access?',
        `${firmName}'s staff will be allowed to use their own AI assistant (Claude) in your DispatchDeck account. ${scopeText()} You can turn this off any time.`,
        { confirmText: 'Approve', danger: false },
      );
      if (!ok) return;
    }
    busy = true; render();
    try {
      await call('setPartnerAiConsent', { grant, firmUid: view.firm.firmUid });
      toast(grant ? `AI access approved for ${firmName}.` : `AI access turned off for ${firmName}.`, 'success');
    } catch (e) {
      toast((e && e.message) || 'Nothing was changed. Try again.', 'error');
    } finally {
      busy = false;
      await load();
    }
  });

  load();
}
